import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

type Row = {
  label: string;
  points: string;
};

type Props = {
  maxTotalScore: string;
  // Receives the calculated score as absolute points, like the total score field.
  onApply: (value: string) => void;
  // Feedback of this submission (grading services and manual entries) that can be
  // taken over as an aspect instead of typing it again.
  feedbackOptions?: any[];
};

// Purely client-side calculation aid: teachers enter deductions per aspect and the
// resulting total score is calculated for them. Nothing here is persisted, only the
// applied total score is saved through the existing form field.
export const ScoreCalculationHelper = (props: Props) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [rows, setRows] = useState<Row[]>([{ label: "", points: "" }]);
  const [feedbackListOpen, setFeedbackListOpen] = useState(false);
  const feedbackDropdownRef = useRef<HTMLDivElement>(null);

  // Close the feedback list when clicking outside of it.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (feedbackDropdownRef.current && !feedbackDropdownRef.current.contains(event.target as Node)) {
        setFeedbackListOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const feedbackOptions = props.feedbackOptions ?? [];

  const getFeedbackLabel = (feedback: any) => {
    const text = (feedback.content ?? "").toString().trim();
    const shortened = text.length > 80 ? text.slice(0, 80) + "…" : text;
    const source = feedback.grader ?? feedback.category ?? "";
    return source === "" ? shortened : shortened + " (" + source + ")";
  };

  // A taken over entry starts without a deduction, so it can also stay a remark that
  // costs no points.
  const takeOverFeedback = (feedback: any) => {
    const label = getFeedbackLabel(feedback);
    const lastRow = rows[rows.length - 1];
    if (lastRow !== undefined && lastRow.label.trim() === "" && lastRow.points.trim() === "") {
      const next = rows.slice();
      next[rows.length - 1] = { label: label, points: "" };
      setRows(next);
    } else {
      setRows([...rows, { label: label, points: "" }]);
    }
    setFeedbackListOpen(false);
  };

  const parsePoints = (value: string) => {
    const num = parseFloat(value.replace(",", "."));
    return isNaN(num) ? 0 : num;
  };

  // An entry that is not a number is ignored by the calculation. Without this check
  // it would be counted as a deduction of zero, so the result would silently stay
  // the same instead of telling the user that the entry was not understood.
  const isInvalidPoints = (value: string) => value.trim() !== "" && isNaN(parseFloat(value.replace(",", ".")));
  const hasInvalidRow = rows.some((row) => isInvalidPoints(row.points));

  const maxNum = parseFloat(props.maxTotalScore);
  const hasValidMax = !isNaN(maxNum) && maxNum > 0;

  const deductionSum = rows.reduce((sum, row) => sum + parsePoints(row.points), 0);
  const absoluteResult = hasValidMax ? Math.min(Math.max(maxNum - deductionSum, 0), maxNum) : 0;
  const roundedAbsolute = Math.round(absoluteResult * 100) / 100;

  const updateRow = (index: number, field: keyof Row, value: string) => {
    const next = rows.slice();
    next[index] = { ...next[index], [field]: value };
    setRows(next);
  };

  const addRow = () => {
    setRows([...rows, { label: "", points: "" }]);
  };

  const removeRow = (index: number) => {
    const next = rows.slice();
    next.splice(index, 1);
    setRows(next.length > 0 ? next : [{ label: "", points: "" }]);
  };

  const apply = () => {
    props.onApply(roundedAbsolute.toString());
  };

  return (
    <div className="field">
      {/* App.css indents every button by 10px, which is wanted between buttons but not
          here, where this one has to line up with the fields above it. */}
      <div className="control is-expanded">
        <button
          className="button is-small is-light is-fullwidth"
          style={{ marginLeft: 0 }}
          onClick={() => setExpanded(!expanded)}
        >
          {t("scoreHelperToggle")}
        </button>
      </div>
      {expanded && (
        <div className="box mt-2" style={{ padding: "1rem" }}>
          <p className="help mb-2">{t("scoreHelperExplanation")}</p>
          {!hasValidMax && <p className="help is-danger">{t("scoreHelperNoMax")}</p>}
          {rows.map((row, index) => (
            <div className="field is-grouped mb-1" key={index}>
              <p className="control is-expanded">
                <input
                  type="text"
                  className="input is-small"
                  placeholder={t("scoreHelperAspect")}
                  value={row.label}
                  onChange={(e) => updateRow(index, "label", e.target.value)}
                />
              </p>
              <p className="control">
                <input
                  type="text"
                  className={"input is-small" + (isInvalidPoints(row.points) ? " is-danger" : "")}
                  style={{ width: "6rem" }}
                  placeholder={t("scoreHelperDeduction")}
                  value={row.points}
                  onChange={(e) => updateRow(index, "points", e.target.value)}
                />
              </p>
              <p className="control">
                <button
                  className="button is-small is-danger is-outlined"
                  style={{ marginLeft: 0 }}
                  onClick={() => removeRow(index)}
                >
                  ✕
                </button>
              </p>
            </div>
          ))}
          {hasInvalidRow && <p className="help is-danger">{t("scoreHelperInvalidDeduction")}</p>}
          <div className="field is-grouped is-grouped-multiline mt-1">
            <p className="control">
              <button className="button is-small is-light" style={{ marginLeft: 0 }} onClick={addRow}>
                {t("scoreHelperAddRow")}
              </button>
            </p>
            <div className="control">
              {/* Opens left: the list is wider than the panel and would spill out of the card. */}
              <div className={`dropdown is-right ${feedbackListOpen ? "is-active" : ""}`} ref={feedbackDropdownRef}>
                <div className="dropdown-trigger">
                  <button
                    type="button"
                    className="button is-small is-light"
                    style={{ marginLeft: 0 }}
                    aria-haspopup="true"
                    disabled={feedbackOptions.length === 0}
                    onClick={() => setFeedbackListOpen((open) => !open)}
                  >
                    <span>{t("scoreHelperTakeOverFeedback")}</span>
                    <span className="icon is-small" style={{ marginLeft: "0.4rem" }}>
                      &#9662;
                    </span>
                  </button>
                </div>
                <div className="dropdown-menu" role="menu" style={{ width: "24rem" }}>
                  <div className="dropdown-content" style={{ maxHeight: "14rem", overflowY: "auto" }}>
                    {feedbackOptions.map((feedback: any, index: number) => (
                      <span
                        key={"helperFeedback" + index}
                        className="dropdown-item"
                        style={{ cursor: "pointer", display: "block", whiteSpace: "normal" }}
                        onClick={() => takeOverFeedback(feedback)}
                      >
                        <span style={{ fontWeight: 600, marginRight: "0.35rem" }}>#{index + 1}</span>
                        {getFeedbackLabel(feedback)}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          {hasValidMax && (
            <p className="mt-3">{t("scoreHelperResult", { score: roundedAbsolute, max: maxNum })}</p>
          )}
          <button
            className="button is-small is-info mt-2"
            style={{ marginLeft: 0 }}
            onClick={apply}
            disabled={!hasValidMax || hasInvalidRow}
          >
            {t("scoreHelperApply")}
          </button>
        </div>
      )}
    </div>
  );
};
